<template>
	<div class="chat-message-content">
		<div v-for="(part, index) in processedContent" :key="index">
			<ChatProductCard v-if="part.type === 'product'" :product="part.product" @view-product="handleViewProduct" />
			<ChatImagePreview
				v-else-if="part.type === 'instagram-marketing-story'"
				:image="part.post.completed_story_url"
				:title="part.post.product_title"
				@view-image="handleViewImage"
			/>
			<ChatMainFeedPost
				v-else-if="part.type === 'instagram-main-feed-post'"
				:post="part.post"
				@copy-success="handleCopySuccess"
				@copy-error="handleCopyError"
			/>
			<div v-else-if="part.type === 'text'" class="message-text" v-html="part.content"></div>
		</div>

		<MessageAttachments
			v-if="message.attachments && message.attachments.length > 0"
			:attachments="message.attachments"
			@preview-image="handlePreviewAttachmentImage"
		/>
	</div>
</template>

<script>
	import ChatProductCard from './ChatProductCard'
	import ChatImagePreview from './ChatImagePreview'
	import ChatMainFeedPost from './ChatMainFeedPost'
	import MessageAttachments from './MessageAttachments'

	const buildPatterns = () => ({
		product: /\[product:(\d+)\]/g,
		story: /\[instagram-marketing-story:(\d+)\]/g,
		mainFeedBlock: /\[instagram-main-feed-post:(\d+)\]([\s\S]*?)\[instagram-main-feed-post:\1\]/g,
	})

	const extractMarkers = content => {
		const { product, story, mainFeedBlock } = buildPatterns()
		const markers = []

		for (const m of content.matchAll(product)) {
			markers.push({
				type: 'product',
				id: parseInt(m[1], 10),
				start: m.index,
				end: m.index + m[0].length,
			})
		}

		for (const m of content.matchAll(story)) {
			markers.push({
				type: 'instagram-marketing-story',
				id: parseInt(m[1], 10),
				start: m.index,
				end: m.index + m[0].length,
			})
		}

		for (const m of content.matchAll(mainFeedBlock)) {
			markers.push({
				type: 'instagram-main-feed-post',
				id: parseInt(m[1], 10),
				start: m.index,
				end: m.index + m[0].length,
				content: m[2],
			})
		}

		markers.sort((a, b) => a.start - b.start)
		return markers
	}

	const segmentContent = (content, markers) => {
		const segments = []
		let cursor = 0

		for (const marker of markers) {
			if (marker.start > cursor) {
				const chunk = content.slice(cursor, marker.start)
				if (chunk.trim()) segments.push({ type: 'text', content: chunk })
			}
			segments.push(marker)
			cursor = marker.end
		}

		if (cursor < content.length) {
			const tail = content.slice(cursor)
			if (tail.trim()) segments.push({ type: 'text', content: tail })
		}
		return segments
	}

	const resolveParts = (segments, { products = [], stories = [] }) => {
		const productMap = new Map(products.map(p => [p.id, p]))
		const storyMap = new Map(stories.map(s => [s.id, s]))

		const out = []
		for (const seg of segments) {
			if (seg.type === 'text') {
				out.push(seg)
				continue
			}
			if (seg.type === 'product') {
				const product = productMap.get(seg.id)
				if (product) out.push({ type: 'product', product })
				continue
			}
			if (seg.type === 'instagram-marketing-story') {
				const post = storyMap.get(seg.id)
				if (post) out.push({ type: 'instagram-marketing-story', post })
				continue
			}
			if (seg.type === 'instagram-main-feed-post' && seg.content) {
				out.push({ type: 'instagram-main-feed-post', post: { id: seg.id, content: seg.content } })
				continue
			}
		}
		return out
	}

	const parseMessageParts = ({ content = '', products = [], stories = [] }) => {
		if (!content || !content.trim()) return []
		const markers = extractMarkers(content)
		const segments = segmentContent(content, markers)
		const parts = resolveParts(segments, { products, stories })
		return parts.length ? parts : [{ type: 'text', content }]
	}

	export default {
		name: 'ChatMessageContent',
		components: { ChatProductCard, ChatImagePreview, ChatMainFeedPost, MessageAttachments },
		props: { message: { type: Object, required: true } },
		computed: {
			processedContent() {
				const content = this.message.content_html || this.message.content || ''
				return parseMessageParts({
					content,
					products: this.message.products || [],
					stories: this.message.instagramMarketingStories || [],
				})
			},
		},
		methods: {
			handleViewProduct(product) {
				this.$emit('view-product', product)
			},
			handleViewPost(post) {
				this.$emit('view-post', post)
			},
			handleViewImage(post) {
				this.$emit('view-image', post)
			},
			handlePreviewAttachmentImage(attachment) {
				this.$emit('preview-attachment-image', attachment)
			},
			handleCopySuccess(data) {
				this.$emit('copy-success', data)
			},
			handleCopyError(data) {
				this.$emit('copy-error', data)
			},
		},
	}
</script>

<style lang="scss" scoped>
	.chat-message-content {
		max-width: 100%;
	}
</style>
